<!--
@component
Small status or label chip with semantic color variants, optional leading icon, loading and highlighted states. Interactive with `onclick` (button) or `href` (anchor). Prefer it, or `Tag` for neutral labels, over new pill markup; `StatusPill` and `AppInfoPill` predate it and keep their own color maps.

@use-instead-of custom rounded-full status pills and badges
@figma https://www.figma.com/design/xb1BaksrwtGhe3JHzX1BbT/Design-System?node-id=1830-1082
@see Tag, FeatureMaturityChip, NewChip, ChipLive
-->
<script module lang="ts">
  export const CHIP_ATTR = "data-chip";

  export const CHIP_VARIANTS = [
    "default",
    "primary",
    "error",
    "warning",
    "yellow", // TODO(jordan): rename yellow to caution
    "accent-pink",
    "accent-blue", // TODO(jordan): rename accent-blue to informational
  ] as const;

  type ChipVariant = (typeof CHIP_VARIANTS)[number];
</script>

<script lang="ts">
  import type { LucideIcon as IconType } from "@lucide/svelte";
  import Loader2 from "@lucide/svelte/icons/loader-circle";
  import type { Snippet } from "svelte";
  import type { SvelteHTMLElements } from "svelte/elements";

  import type { TooltipProps } from "$lib/design-systems/product/primitives/overlays/tooltip";
  import tooltip from "$lib/design-systems/product/primitives/overlays/tooltip";

  interface SharedProps {
    variant?: ChipVariant;
    iconLeft?: IconType;
    loading?: boolean;
    ellipsis?: boolean;
    isHighlighted?: boolean;
    tooltip?: string | TooltipProps | false;
    children?: Snippet;
    iconClass?: string;
  }

  // `onclick` is re-declared on the span branch because the handler is also
  // invoked from the keyboard fallback, which has no mouse event to forward.
  type Props =
    | (SharedProps & SvelteHTMLElements["a"] & { href: string })
    | (SharedProps &
        Omit<SvelteHTMLElements["span"], "onclick"> & {
          href?: never;
          onclick?: () => void;
        });

  // Each variant maps to a semantic color role; `default` (secondary) is the
  // base recipe, so it needs no modifier class.
  const chipRoleClass: Record<ChipVariant, string | undefined> = {
    default: undefined,
    primary: "chip-success",
    error: "chip-danger",
    warning: "chip-warning",
    yellow: "chip-caution",
    "accent-pink": "chip-running",
    "accent-blue": "chip-informational",
  };

  let {
    variant = "default",
    iconLeft,
    loading = false,
    ellipsis = false,
    isHighlighted = false,
    tooltip: tooltipProps,
    class: classOverride,
    iconClass: iconClassOverride,
    children,
    ...rest
  }: Props = $props();

  // Narrow before reading `onclick` — the union still includes the anchor
  // handler type until `href` is checked.
  let spanOnclick = $derived.by(() => {
    if (rest.href !== undefined) return undefined;
    return rest.onclick;
  });
  let isLink = $derived(rest.href !== undefined);
  let isInteractive = $derived(isLink || spanOnclick !== undefined);
  // `rest` is the union of both attribute sets, which is too wide to spread
  // onto a span. Each branch only renders for the props that produced it, so
  // narrow to the element actually being rendered.
  let spanRest = $derived(rest as Omit<SvelteHTMLElements["span"], "onclick">);
  let chipClass = $derived([
    "chip",
    "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-normal",
    ellipsis ? "overflow-hidden" : "shrink-0",
    isInteractive && "cursor-pointer select-none",
    isInteractive && "chip-interactive",
    isHighlighted && "chip-highlighted",
    chipRoleClass[variant],
    // Caller classes come last so they can override the variant recipe.
    classOverride,
  ]);
</script>

{#snippet content()}
  <!-- An icon-only chip has nothing to space away from, so it keeps the
  symmetric horizontal padding instead of a trailing margin. -->
  {@const iconSpacing = children ? "mr-1" : undefined}
  {#if loading}
    <span class={["icon shrink-0 animate-spin", iconSpacing]}>
      <Loader2 size={12} />
    </span>
  {:else if iconLeft}
    {@const Icon = iconLeft}
    <span class={["icon shrink-0", iconSpacing, iconClassOverride]}
      ><Icon size={12} /></span
    >
  {/if}
  {#if ellipsis}
    <span class="min-w-0 truncate">{@render children?.()}</span>
  {:else}
    {@render children?.()}
  {/if}
{/snippet}

{#if isLink}
  <a
    {...rest}
    class={chipClass}
    use:tooltip={tooltipProps}
    {...{ [CHIP_ATTR]: "" }}
  >
    {@render content()}
  </a>
{:else}
  <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
  <span
    {...spanRest}
    class={chipClass}
    use:tooltip={tooltipProps}
    onclick={spanOnclick}
    onkeydown={spanOnclick
      ? (e: KeyboardEvent) => {
          if (
            e.target === e.currentTarget &&
            (e.key === "Enter" || e.key === " ")
          ) {
            e.preventDefault();
            spanOnclick();
          }
        }
      : undefined}
    role={spanOnclick ? "button" : undefined}
    tabindex={spanOnclick ? 0 : undefined}
    {...{ [CHIP_ATTR]: "" }}
  >
    {@render content()}
  </span>
{/if}

<style>
  @layer components {
    .chip {
      --chip-text: var(--semantic-text-secondary);
      --chip-bg: var(--semantic-background-secondary);
      --chip-icon: var(--semantic-icon-secondary);
      --chip-text-hover: var(--semantic-text-secondary-hover);
      --chip-bg-hover: var(--semantic-background-secondary-hover);
      --chip-icon-hover: var(--semantic-icon-secondary-hover);
      --chip-text-active: var(--semantic-text-secondary-active);
      --chip-bg-active: var(--semantic-background-secondary-active);
      --chip-icon-active: var(--semantic-icon-secondary-active);

      color: var(--chip-text);
      background-color: var(--chip-bg);

      :global(.icon) {
        color: var(--chip-icon);
      }

      /* Only interactive chips react to hover/active. */
      &.chip-interactive {
        &:hover {
          color: var(--chip-text-hover);
          background-color: var(--chip-bg-hover);
          :global(.icon) {
            color: var(--chip-icon-hover);
          }
        }
        &:active {
          color: var(--chip-text-active);
          background-color: var(--chip-bg-active);
          :global(.icon) {
            color: var(--chip-icon-active);
          }
        }
      }

      /* :global() prevents Svelte from stripping these as "unused CSS" — the
         class names come from a dynamic object lookup (chipRoleClass[variant])
         that the compiler can't statically trace. */
      &:global(.chip-success) {
        --chip-text: var(--semantic-text-success);
        --chip-bg: var(--semantic-background-success);
        --chip-icon: var(--semantic-icon-success);
        --chip-text-hover: var(--semantic-text-success-hover);
        --chip-bg-hover: var(--semantic-background-success-hover);
        --chip-icon-hover: var(--semantic-icon-success-hover);
        --chip-text-active: var(--semantic-text-success-active);
        --chip-bg-active: var(--semantic-background-success-active);
        --chip-icon-active: var(--semantic-icon-success-active);
      }

      &:global(.chip-danger) {
        --chip-text: var(--semantic-text-danger);
        --chip-bg: var(--semantic-background-danger);
        --chip-icon: var(--semantic-icon-danger);
        --chip-text-hover: var(--semantic-text-danger-hover);
        --chip-bg-hover: var(--semantic-background-danger-hover);
        --chip-icon-hover: var(--semantic-icon-danger-hover);
        --chip-text-active: var(--semantic-text-danger-active);
        --chip-bg-active: var(--semantic-background-danger-active);
        --chip-icon-active: var(--semantic-icon-danger-active);
      }

      &:global(.chip-warning) {
        --chip-text: var(--semantic-text-warning);
        --chip-bg: var(--semantic-background-warning);
        --chip-icon: var(--semantic-icon-warning);
        --chip-text-hover: var(--semantic-text-warning-hover);
        --chip-bg-hover: var(--semantic-background-warning-hover);
        --chip-icon-hover: var(--semantic-icon-warning-hover);
        --chip-text-active: var(--semantic-text-warning-active);
        --chip-bg-active: var(--semantic-background-warning-active);
        --chip-icon-active: var(--semantic-icon-warning-active);
      }

      &:global(.chip-caution) {
        --chip-text: var(--semantic-text-caution);
        --chip-bg: var(--semantic-background-caution);
        --chip-icon: var(--semantic-icon-caution);
        --chip-text-hover: var(--semantic-text-caution-hover);
        --chip-bg-hover: var(--semantic-background-caution-hover);
        --chip-icon-hover: var(--semantic-icon-caution-hover);
        --chip-text-active: var(--semantic-text-caution-active);
        --chip-bg-active: var(--semantic-background-caution-active);
        --chip-icon-active: var(--semantic-icon-caution-active);
      }

      &:global(.chip-running) {
        --chip-text: var(--semantic-text-running);
        --chip-bg: var(--semantic-background-running);
        --chip-icon: var(--semantic-icon-running);
        --chip-text-hover: var(--semantic-text-running-hover);
        --chip-bg-hover: var(--semantic-background-running-hover);
        --chip-icon-hover: var(--semantic-icon-running-hover);
        --chip-text-active: var(--semantic-text-running-active);
        --chip-bg-active: var(--semantic-background-running-active);
        --chip-icon-active: var(--semantic-icon-running-active);
      }

      &:global(.chip-informational) {
        --chip-text: var(--semantic-text-informational);
        --chip-bg: var(--semantic-background-informational);
        --chip-icon: var(--semantic-icon-informational);
        --chip-text-hover: var(--semantic-text-informational-hover);
        --chip-bg-hover: var(--semantic-background-informational-hover);
        --chip-icon-hover: var(--semantic-icon-informational-hover);
        --chip-text-active: var(--semantic-text-informational-active);
        --chip-bg-active: var(--semantic-background-informational-active);
        --chip-icon-active: var(--semantic-icon-informational-active);
      }
    }

    .chip-highlighted {
      box-shadow: 0 0 0 1px var(--semantic-border-primary-selected);
    }
  }
</style>
